import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../../lib/api';
import type { UomItem } from '../product/types';

/**
 * 计量单位字典管理（架构书 §11.3："系统设置、原因码、单位、位置类型"归
 * settings.manage）——与 location-types-panel.tsx 同一模式、同一权限依据。
 * 这是产品能选到基本单位的前提：新公司首次使用时，必须先在这里建至少
 * 一个 COUNT 单位（如"支"/"piece"），产品创建表单才有东西可选。
 */
export function UomsPanel(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ['uoms'],
    queryFn: () => apiFetch<{ items: UomItem[] }>('/uoms'),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [code, setCode] = useState('');
  const [nameKey, setNameKey] = useState('');
  const [symbol, setSymbol] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const create = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch('/uoms', {
        method: 'POST',
        body: { code, nameKey, symbol: symbol || undefined },
      });
      setShowCreate(false);
      setCode('');
      setNameKey('');
      setSymbol('');
      await queryClient.invalidateQueries({ queryKey: ['uoms'] });
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section">
      <div className="row row--between">
        <h2>{t('uom.title')}</h2>
        <button className="btn btn--primary btn--sm" onClick={() => setShowCreate(true)}>
          {t('uom.create')}
        </button>
      </div>

      {isPending && <p>{t('app.loading')}</p>}
      {data && (
        <div className="table-scroll"><table className="table">
          <thead>
            <tr>
              <th>{t('uom.colCode')}</th>
              <th>{t('uom.colName')}</th>
              <th>{t('uom.colSymbol')}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((u) => (
              <tr key={u.id} style={u.archivedAt ? { opacity: 0.5 } : undefined}>
                <td>{u.code}</td>
                <td>{t(u.nameKey, u.code)}</td>
                <td>{u.symbol ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void create(e)}
          >
            <h2>{t('uom.create')}</h2>
            <label className="field">
              <span>{t('uom.colCode')}</span>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                placeholder="piece"
              />
            </label>
            <label className="field">
              <span>{t('uom.colName')}</span>
              <input
                value={nameKey}
                onChange={(e) => setNameKey(e.target.value)}
                required
                placeholder="uom.custom.piece"
              />
            </label>
            <label className="field">
              <span>{t('uom.colSymbol')}</span>
              <input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="pc" />
            </label>
            {error && <p className="field-error">{error}</p>}
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setShowCreate(false)}>
                {t('app.cancel')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={submitting}>
                {t('uom.create')}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
